import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Card } from "@/components/ui/Card";
import {
  ProjectStatusBadge,
  ProcurementMethodBadge,
} from "@/components/shared/StatusBadge";
import { DateDisplay } from "@/components/shared/DateDisplay";
import { PROVINCE_NAMES } from "@/lib/constants";
import { cn } from "@/lib/utils";
import type { VTendersPublic } from "@/lib/pocketbase";

interface TenderCardProps {
  tender: VTendersPublic;
  className?: string;
}

function formatTenderCardBudget(satang: number): string {
  const baht = Math.round(satang / 100);

  if (Math.abs(baht) >= 1_000_000) {
    const million = baht / 1_000_000;
    return `${new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(million)} ล้าน`;
  }

  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(baht);
}

export function TenderCard({ tender, className }: TenderCardProps) {
  const provinceCode = tender.province_code?.trim();
  const provinceLabel = provinceCode
    ? PROVINCE_NAMES[provinceCode] ||
      PROVINCE_NAMES[provinceCode.toLowerCase()] ||
      provinceCode
    : null;
  const budgetLabel = tender.sum_price_agree
    ? formatTenderCardBudget(tender.sum_price_agree)
    : null;

  return (
    <Link to={`/tenders/${tender.tender_id}`}>
      <Card
        hover
        className={cn("transition-all duration-200 group", className)}
      >
        {/* Header row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Project name */}
            <h3 className="font-display font-semibold text-slate-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
              {tender.project_name}
            </h3>

            {/* Agency info */}
            <div className="flex items-center gap-2 mt-1.5">
              <Icon
                icon="solar:buildings-line-duotone"
                className="w-4 h-4 text-slate-400 flex-shrink-0"
              />
              <span className="text-sm text-slate-600 truncate">
                {tender.dept_name || "ไม่ระบุหน่วยงาน"}
              </span>
            </div>
          </div>

          {/* Status badge */}
          <ProjectStatusBadge status={tender.project_status as any} />
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-slate-100">
          {/* Budget */}
          {budgetLabel && (
            <div className="flex items-center gap-1.5">
              <Icon
                icon="solar:hand-money-line-duotone"
                className="w-4 h-4 text-slate-400"
              />
              <div className="flex items-baseline gap-1">
                <span className="font-medium text-slate-700">{budgetLabel}</span>
                <span className="text-[10px] text-slate-500 leading-none">
                  บาท
                </span>
              </div>
            </div>
          )}

          {/* Published date */}
          {tender.published_at && (
            <div className="flex items-center gap-1.5 text-sm text-slate-500">
              <Icon
                icon="solar:calendar-line-duotone"
                className="w-4 h-4 text-slate-400"
              />
              <DateDisplay date={tender.published_at} />
            </div>
          )}

          {/* Province */}
          {provinceLabel && (
            <div className="flex items-center gap-1.5 text-sm text-slate-500">
              <Icon
                icon="solar:map-point-line-duotone"
                className="w-4 h-4 text-slate-400"
              />
              <span>{provinceLabel}</span>
            </div>
          )}

          {tender.procurement_method && (
            <ProcurementMethodBadge method={tender.procurement_method} />
          )}
        </div>

        {(tender.project_id || tender.version_number || tender.published_at) && (
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
            {tender.project_id && <span>รหัส: {tender.project_id}</span>}
            {tender.version_number && <span>v{tender.version_number}</span>}
            {tender.published_at && (
              <span>
                เผยแพร่: <DateDisplay date={tender.published_at} format="relative" />
              </span>
            )}
          </div>
        )}
      </Card>
    </Link>
  );
}

// Compact table row version for Pro mode
interface TenderRowProps {
  tender: VTendersPublic;
  className?: string;
}

export function TenderRow({ tender, className }: TenderRowProps) {
  const budgetLabel = tender.sum_price_agree
    ? formatTenderCardBudget(tender.sum_price_agree)
    : null;

  return (
    <Link
      to={`/tenders/${tender.tender_id}`}
      className={cn(
        "grid grid-cols-12 gap-4 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0",
        className,
      )}
    >
      {/* Project name - 5 cols */}
      <div className="col-span-5 min-w-0">
        <p className="font-medium text-slate-900 truncate hover:text-blue-600">
          {tender.project_name}
        </p>
        <p className="text-xs text-slate-500 truncate mt-0.5">
          {tender.dept_name}
        </p>
      </div>

      {/* Status - 2 cols */}
      <div className="col-span-2 flex items-center">
        <ProjectStatusBadge status={tender.project_status as any} />
      </div>

      {/* Budget - 2 cols */}
      <div className="col-span-2 flex items-center">
        {budgetLabel ? (
          <div className="inline-flex items-baseline gap-1">
            <span className="font-medium text-slate-700">{budgetLabel}</span>
            <span className="text-[10px] text-slate-500 leading-none">บาท</span>
          </div>
        ) : (
          <span className="text-slate-400">-</span>
        )}
      </div>

      {/* Date - 2 cols */}
      <div className="col-span-2 flex items-center text-sm text-slate-600">
        {tender.published_at ? (
          <DateDisplay date={tender.published_at} />
        ) : (
          <span className="text-slate-400">-</span>
        )}
      </div>

      {/* Actions - 1 col */}
      <div className="col-span-1 flex items-center justify-end">
        <Icon
          icon="solar:alt-arrow-right-line-duotone"
          className="w-5 h-5 text-slate-300"
        />
      </div>
    </Link>
  );
}

// Empty state
export function TenderEmptyState() {
  return (
    <Card className="py-12 text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <Icon
          icon="solar:document-text-line-duotone"
          className="w-8 h-8 text-slate-400"
        />
      </div>
      <h3 className="font-display font-semibold text-slate-900">ไม่พบประกาศ</h3>
      <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
        ลองปรับตัวกรองหรือคำค้นหาของคุณเพื่อหาผลลัพธ์เพิ่มเติม
      </p>
    </Card>
  );
}
